// The one screen: services, headline numbers, alarms, Lambdas, gateways, timeline, operator log.
import React from 'react';
import type { AlarmInfo, LambdaIdentity, Metrics, Overview as OverviewData, TimelineEvent } from '../../../shared/types';
import { api } from '../api/client';
import { GAsyncState, GPill, GStatus, inputStyle, useAsyncTask } from '../design/atoms';
import { Empty, SectionCard } from '../design/Section';
import { Sparkline } from '../design/Sparkline';
import { StatTile } from '../design/StatTile';
import { fmtAge, fmtBytes, fmtCount, fmtDateTime, fmtMs, fmtPct, fmtUnit } from '../fmt';
import { useEnvStore } from '../store/envStore';
import { useActGate } from '../design/useAct';

function series(m: Metrics | undefined, id: string) { return m?.series[id]; }
function sumOf(m: Metrics | undefined, ids: string[]): number { return ids.reduce((s, id) => s + (m?.series[id]?.sum ?? 0), 0); }
function mergePoints(m: Metrics | undefined, ids: string[]): Array<[number, number]> {
  const acc = new Map<number, number>();
  for (const id of ids) for (const [t, v] of m?.series[id]?.points ?? []) acc.set(t, (acc.get(t) ?? 0) + v);
  return [...acc.entries()].sort((a, b) => a[0] - b[0]);
}


function AlarmsCard({ data, reload }: { data: OverviewData; reload: () => void }) {
  const gate = useActGate();
  const task = useAsyncTask();
  const [acking, setAcking] = React.useState<AlarmInfo | null>(null);
  const [note, setNote] = React.useState('');
  const ack = (a: AlarmInfo) => void task.run(`Acknowledging ${a.name}…`, async () => {
    await api(`/api/env/${data.env.name}/alarms/ack`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name: a.name, note: note || null, since: a.since }) });
    setAcking(null); setNote(''); reload();
  });
  return (
    <SectionCard title="Alarms" hint="created by this dashboard's stack from the server team's alarm set; open first" section={data.alarms} onRetry={reload}>
      {(rows) => {
        const open = rows.filter((a) => a.state !== 'OK');
        return (
          <>
            <GAsyncState busy={task.busy} error={task.error} label={task.label} onRetry={task.retry} />
            {rows.length === 0 ? <Empty>No alarms found for this environment.</Empty> : open.length === 0
              ? <Empty>All {rows.length} alarms are OK.</Empty>
              : (
                <table className="pd">
                  <thead><tr><th>State</th><th>Alarm</th><th>Since</th><th>Reason</th><th></th></tr></thead>
                  <tbody>{open.map((a) => (
                    <React.Fragment key={a.name}>
                      <tr>
                        <td style={{ whiteSpace: 'nowrap' }}><GStatus kind={a.state === 'ALARM' ? 'err' : 'dim'} label={a.state === 'ALARM' ? 'alarm' : 'no data'} /></td>
                        <td><div style={{ fontFamily: 'var(--ff-code)', fontSize: 12.5 }}>{a.name.replace(`projector-${data.env.name}-`, '')}</div><div style={{ color: 'var(--fg-4)', fontSize: 12 }}>{a.description}</div></td>
                        <td style={{ whiteSpace: 'nowrap' }} title={a.since}>{a.since ? fmtAge(a.since) : '–'}</td>
                        <td style={{ fontFamily: 'var(--ff-code)', fontSize: 11.5, color: 'var(--fg-3)', maxWidth: 420 }}>{a.reason}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {a.ack ? <span style={{ fontFamily: 'var(--ff-code)', fontSize: 11.5, color: 'var(--fg-3)' }} title={a.ack.note ?? ''}>acked by {a.ack.by} {fmtAge(a.ack.at)}</span>
                            : <GPill onClick={() => setAcking(a)} disabled={!gate.allowed} title={gate.why}>Acknowledge</GPill>}
                        </td>
                      </tr>
                      {acking?.name === a.name && (
                        <tr><td colSpan={5} style={{ background: 'var(--bg-base)' }}>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <input autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional note: what you found or did" style={{ ...inputStyle, padding: '7px 12px' }} onKeyDown={(e) => { if (e.key === 'Enter') ack(a); if (e.key === 'Escape') setAcking(null); }} />
                            <GPill solid onClick={() => ack(a)} disabled={task.busy}>Confirm</GPill>
                            <GPill onClick={() => setAcking(null)}>Cancel</GPill>
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}</tbody>
                </table>
              )}
            {open.length > 0 && rows.length > open.length && <div style={{ fontFamily: 'var(--ff-code)', fontSize: 11.5, color: 'var(--fg-4)', marginTop: 8 }}>{rows.length - open.length} more are OK.</div>}
          </>
        );
      }}
    </SectionCard>
  );
}

function TimelineCard({ data, reload }: { data: OverviewData; reload: () => void }) {
  const gate = useActGate();
  const task = useAsyncTask();
  const [text, setText] = React.useState('');
  const add = () => { if (!text.trim()) return; void task.run('Saving note…', async () => {
    await api(`/api/env/${data.env.name}/notes`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text: text.trim() }) });
    setText(''); reload();
  }); };
  const kind = (e: TimelineEvent): 'ok' | 'warn' | 'err' | 'dim' | 'work' => e.kind === 'deploy' ? 'work' : e.kind === 'config-drift' ? 'warn' : e.kind === 'reset' ? 'err' : 'dim';
  return (
    <SectionCard title="Timeline" hint="deploys and drift from the jobs Lambda, resets, and notes" section={data.timeline} onRetry={reload}>
      {(rows) => (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} placeholder={gate.allowed ? `Leave a note on ${data.env.name} (what happened, what you did)` : gate.why} disabled={!gate.allowed} title={gate.why} style={{ ...inputStyle, padding: '7px 12px', opacity: gate.allowed ? 1 : 0.6 }} />
            <GPill onClick={add} disabled={!gate.allowed || !text.trim() || task.busy} title={gate.why}>Add note</GPill>
          </div>
          <GAsyncState busy={task.busy} error={task.error} label={task.label} onRetry={task.retry} />
          {rows.length === 0 ? <Empty>Nothing yet. Deploys appear once the jobs Lambda has seen two different code hashes.</Empty> : (
            <table className="pd">
              <tbody>{rows.map((e) => (
                <tr key={e.id}>
                  <td style={{ whiteSpace: 'nowrap', width: 150 }} title={e.at}>{fmtDateTime(e.at)}</td>
                  <td style={{ whiteSpace: 'nowrap', width: 110 }}><GStatus kind={kind(e)} label={e.kind} /></td>
                  <td>{e.title}{e.detail && <div style={{ fontFamily: 'var(--ff-code)', fontSize: 11.5, color: 'var(--fg-3)', marginTop: 2 }}>{e.detail}</div>}</td>
                  <td style={{ whiteSpace: 'nowrap', color: 'var(--fg-4)', fontSize: 12 }}>{e.by ?? ''}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </>
      )}
    </SectionCard>
  );
}

export function Overview() {
  const { env, overview, loading, error, load } = useEnvStore();
  const data: OverviewData | undefined = overview[env];
  const m = data?.metrics.ok ? data.metrics.data : undefined;
  const lambdas = data?.lambdas.ok ? data.lambdas.data : [];
  const lambdaIds = lambdas.map((l) => l.id);
  const windowLabel = m ? `last ${m.window}` : '';
  const reload = () => void load();

  if (!data) {
    return <div style={{ padding: 28 }}><GAsyncState busy={loading} error={error} label={`Reading ${env}…`} onRetry={reload} /></div>;
  }

  const errors = sumOf(m, lambdaIds.map((id) => `lambda_${id}_errors`));
  const throttles = sumOf(m, lambdaIds.map((id) => `lambda_${id}_throttles`));
  const invocations = sumOf(m, lambdaIds.map((id) => `lambda_${id}_invocations`));
  const wsConnects = sumOf(m, ['ws_ws_connect']);
  const iotAuth = sumOf(m, ['iot_authn_error_mqtt', 'iot_authn_error_http']);
  const cpu = series(m, 'rds_cpu'); const conns = series(m, 'rds_connections'); const storage = series(m, 'rds_free_storage');
  const openAlarms = data.alarms.ok ? data.alarms.data.filter((a) => a.state === 'ALARM').length : null;
  const uptime = data.canaries.ok ? data.canaries.data.uptime : {};

  return (
    <div style={{ padding: '22px 28px 40px', maxWidth: 1400 }}>
      {error && <div style={{ marginBottom: 14 }}><GAsyncState busy={false} error={error} onRetry={reload} /></div>}

      {data.samples.ok && data.samples.data.latest && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(196px, 1fr))', gap: 12, marginBottom: 12 }}>
          {([['users', 'Users'], ['studios', 'Studios'], ['projects', 'Projects'], ['machines', 'Machines'], ['invitesPending', 'Pending invites'], ['cutOff', 'Cut off']] as const).map(([k, label]) => (
            <StatTile key={k} label={label} value={fmtCount(data.samples.ok ? data.samples.data.latest!.counts[k] : null)} tone={k === 'cutOff' && data.samples.ok && data.samples.data.latest!.counts[k] > 0 ? 'alert' : 'normal'}
              points={data.samples.ok ? data.samples.data.series[k] : undefined} format={fmtCount} sub={`sampled ${fmtAge(data.samples.ok ? data.samples.data.latest!.at : null)}`} />
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(196px, 1fr))', gap: 12, marginBottom: 18 }}>
        <StatTile label="Open alarms" value={openAlarms === null ? '–' : String(openAlarms)} tone={openAlarms ? 'alert' : 'normal'} sub={data.alarms.ok ? `${data.alarms.data.length} defined` : 'unavailable'} />
        <StatTile label={`Lambda errors · ${windowLabel}`} value={fmtCount(errors)} tone={errors > 0 ? 'alert' : 'normal'} points={mergePoints(m, lambdaIds.map((id) => `lambda_${id}_errors`))} format={fmtCount} sub="all functions" />
        <StatTile label={`Throttles · ${windowLabel}`} value={fmtCount(throttles)} tone={throttles > 0 ? 'alert' : 'normal'} points={mergePoints(m, lambdaIds.map((id) => `lambda_${id}_throttles`))} format={fmtCount} sub="all functions" />
        <StatTile label={`Invocations · ${windowLabel}`} value={fmtCount(invocations)} points={mergePoints(m, lambdaIds.map((id) => `lambda_${id}_invocations`))} format={fmtCount} sub="all functions" />
        <StatTile label={`WebSocket connects · ${windowLabel}`} value={fmtCount(wsConnects)} points={series(m, 'ws_ws_connect')?.points} format={fmtCount} sub="presence proxy" />
        <StatTile label={`IoT auth failures · ${windowLabel}`} value={fmtCount(iotAuth)} tone={iotAuth > 0 ? 'alert' : 'normal'} points={mergePoints(m, ['iot_authn_error_mqtt', 'iot_authn_error_http'])} format={fmtCount} sub={`${fmtCount(sumOf(m, ['iot_connect_success_mqtt']))} MQTT connects`} />
        <StatTile label="RDS CPU" value={fmtPct(cpu?.last)} points={cpu?.points} format={fmtPct} sub={`peak ${fmtPct(cpu?.max)}`} />
        <StatTile label="RDS connections" value={fmtCount(conns?.last)} points={conns?.points} format={fmtCount} sub={`peak ${fmtCount(conns?.max)}`} />
        <StatTile label="RDS free storage" value={fmtBytes(storage?.last)} points={storage?.points} format={fmtBytes} sub="minimum in window" />
      </div>

      <AlarmsCard data={data} reload={reload} />

      <SectionCard title="Services" hint={`probed just now · uptime over the last ${data.canaries.ok ? data.canaries.data.window : '…'} from the 15-minute canaries`} section={data.health} onRetry={reload}>
        {(rows) => (
          <table className="pd">
            <thead><tr><th>Service</th><th>State</th><th className="num">Status</th><th className="num">Latency</th><th className="num">Uptime</th><th className="num">Median</th><th>Build</th><th>Endpoint</th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const u = uptime[r.id];
                return (
                  <tr key={r.id}>
                    <td style={{ fontFamily: 'var(--ff-code)' }}>{r.label}</td>
                    <td><GStatus kind={r.ok ? 'ok' : 'err'} label={r.ok ? 'up' : 'down'} /> <span style={{ color: 'var(--fg-4)', fontFamily: 'var(--ff-code)', fontSize: 12 }}>{r.note}</span></td>
                    <td className="num">{r.status ?? '–'}</td>
                    <td className="num">{fmtMs(r.ms)}</td>
                    <td className="num" style={{ color: u && u.failures > 0 ? 'var(--s-err-fg)' : undefined }}>{u && u.checks ? `${((1 - u.failures / u.checks) * 100).toFixed(u.checks > 50 ? 2 : 1)}%` : '–'}{u && u.checks ? <span style={{ color: 'var(--fg-4)' }}> /{u.checks}</span> : null}</td>
                    <td className="num">{u?.medianMs != null ? fmtMs(u.medianMs) : '–'}</td>
                    <td style={{ fontFamily: 'var(--ff-code)', fontSize: 12 }} title={r.buildSha ? `${r.buildSha} built ${r.buildTime}` : undefined}>{r.buildSha ? <>{r.buildSha.slice(0, 10)} <span style={{ color: 'var(--fg-4)' }}>{r.buildTime ? fmtAge(r.buildTime) : ''}</span></> : <span style={{ color: 'var(--fg-5)' }}>–</span>}</td>
                    <td style={{ fontFamily: 'var(--ff-code)', fontSize: 12, color: 'var(--fg-3)', overflowWrap: 'anywhere' }}>{r.url}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </SectionCard>

      <SectionCard title="Lambdas" hint="deploy identity is the code hash; server_version is 0.1.0 on every build" section={data.lambdas} onRetry={reload}>
        {(rows: LambdaIdentity[]) => (
          <div style={{ overflowX: 'auto' }}>
            <table className="pd">
              <thead><tr><th>Function</th><th>Owner</th><th>Deployed</th><th>Code</th><th>Runtime</th><th className="num">Mem</th><th className="num">Timeout</th><th className="num">Reserved</th><th className="num">Errors</th><th className="num">Throttles</th><th className="num">p99</th><th>Invocations</th></tr></thead>
              <tbody>
                {rows.map((l) => {
                  const err = series(m, `lambda_${l.id}_errors`); const thr = series(m, `lambda_${l.id}_throttles`); const p99 = series(m, `lambda_${l.id}_p99`); const inv = series(m, `lambda_${l.id}_invocations`);
                  return (
                    <tr key={l.id}>
                      <td><div style={{ fontFamily: 'var(--ff-code)' }}>{l.fn}</div><div style={{ color: 'var(--fg-4)', fontSize: 12 }}>{l.label}{l.state !== 'Active' ? ` · ${l.state}` : ''}{l.lastUpdateStatus && l.lastUpdateStatus !== 'Successful' ? ` · ${l.lastUpdateStatus}` : ''}</div></td>
                      <td style={{ color: 'var(--fg-3)' }}>{l.owner}</td>
                      <td title={l.lastModified}><div>{fmtAge(l.lastModified)}</div><div style={{ color: 'var(--fg-4)', fontSize: 12, fontFamily: 'var(--ff-code)' }}>{fmtDateTime(l.lastModified)}</div></td>
                      <td title={l.codeSha256} style={{ fontFamily: 'var(--ff-code)', fontSize: 12 }}>{l.codeSha256.slice(0, 10)}</td>
                      <td style={{ fontFamily: 'var(--ff-code)', fontSize: 12, color: 'var(--fg-3)' }}>{l.runtime} {l.architectures.join('/')}</td>
                      <td className="num">{l.memoryMb} MB</td>
                      <td className="num">{l.timeoutS} s</td>
                      <td className="num">{l.reservedConcurrency ?? '–'}</td>
                      <td className="num" style={{ color: (err?.sum ?? 0) > 0 ? 'var(--s-err-fg)' : undefined }}>{fmtCount(err?.sum)}</td>
                      <td className="num" style={{ color: (thr?.sum ?? 0) > 0 ? 'var(--s-err-fg)' : undefined }}>{fmtCount(thr?.sum)}</td>
                      <td className="num">{fmtMs(p99?.max)}</td>
                      <td><Sparkline points={inv?.points ?? []} width={110} height={22} title={`${l.fn} invocations`} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Gateways and IoT" hint={windowLabel} section={data.metrics} onRetry={reload}>
        {(mt: Metrics) => {
          const groups = new Map<string, Metrics['series'][string][]>();
          for (const s of Object.values(mt.series)) if (s.group.startsWith('api:') || s.group.startsWith('ws:') || s.group === 'iot') groups.set(s.group, [...(groups.get(s.group) ?? []), s]);
          return (
            <table className="pd">
              <thead><tr><th>Source</th><th>Measure</th><th className="num">Total</th><th className="num">Peak</th><th>Trend</th></tr></thead>
              <tbody>
                {[...groups.entries()].flatMap(([, list]) => list.map((s, i) => (
                  <tr key={s.id}>
                    <td style={{ fontFamily: 'var(--ff-code)', color: i === 0 ? undefined : 'transparent' }}>{s.group === 'iot' ? 'IoT Core' : s.label.split(' ')[0]}</td>
                    <td>{s.group === 'iot' ? s.label.replace(/^IoT /, '') : s.label.slice(s.label.indexOf(' ') + 1)}</td>
                    <td className="num" style={{ color: /5xx|error|failure|throttle/i.test(s.label) && s.sum > 0 ? 'var(--s-err-fg)' : undefined }}>{s.unit === 'ms' ? '–' : fmtCount(s.sum)}</td>
                    <td className="num">{fmtUnit(s.max, s.unit)}</td>
                    <td><Sparkline points={s.points} width={110} height={22} title={s.label} /></td>
                  </tr>
                )))}
              </tbody>
            </table>
          );
        }}
      </SectionCard>

      <TimelineCard data={data} reload={reload} />

      <SectionCard title="Operator log" hint="every action taken from this dashboard" section={data.actions} onRetry={reload}>
        {(rows) => rows.length === 0
          ? <Empty>No actions yet. Acknowledging an alarm or leaving a note lands here.</Empty>
          : (
            <table className="pd">
              <thead><tr><th>When</th><th>Who</th><th>Env</th><th>Action</th><th>Target</th><th>Result</th></tr></thead>
              <tbody>{rows.map((a) => (
                <tr key={a.id}><td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(a.at)}</td><td>{a.by}</td><td>{a.env}</td><td style={{ fontFamily: 'var(--ff-code)', fontSize: 12 }}>{a.kind}</td><td style={{ fontFamily: 'var(--ff-code)', fontSize: 12 }}>{a.target}</td><td><GStatus kind={a.result === 'ok' ? 'ok' : 'err'} label={a.result} /> <span style={{ color: 'var(--fg-3)', fontSize: 12 }}>{a.detail}</span></td></tr>
              ))}</tbody>
            </table>
          )}
      </SectionCard>
    </div>
  );
}
